import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface AddUserFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (user: {
    firstName: string;
    lastName: string;
    email: string;
    password?: string;
    role: string;
    title?: string;
    businessUnit?: string;
    phone?: string;
    userType: 'INTERNAL' | 'EXTERNAL';
    image?: string;
    /** Only when inviteOnly: optional message included in the invite email. */
    customMessage?: string;
  }) => void;
  error: string | null;
  /** When true, form is for inviting a team member (no password; they set it via email link). */
  inviteOnly?: boolean;
}

export function AddUserForm({ isOpen, onClose, onSubmit, error, inviteOnly = false }: AddUserFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('USER');
  const [title, setTitle] = useState('');
  const [businessUnit, setBusinessUnit] = useState('');
  const [phone, setPhone] = useState('');
  const [image, setImage] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      firstName,
      lastName,
      email,
      ...(inviteOnly ? {} : { password }),
      role,
      ...(inviteOnly
        ? (customMessage.trim() ? { customMessage: customMessage.trim() } : {})
        : { title, businessUnit, phone, image }),
      userType: 'INTERNAL',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{inviteOnly ? 'Invite team member' : 'Add New User'}</DialogTitle>
        </DialogHeader>
        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="firstName" className="text-right">
                First Name
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="lastName" className="text-right">
                Last Name
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            {!inviteOnly && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="password" className="text-right">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="col-span-3"
                  required
                />
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="role" className="text-right">
                Role
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USER">User</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {inviteOnly && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="customMessage" className="text-right">
                  Personal message
                </Label>
                <Textarea
                  id="customMessage"
                  placeholder="Optional message to include in the invitation email…"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  className="col-span-3 min-h-[80px] resize-y"
                  rows={3}
                />
              </div>
            )}
            {!inviteOnly && (
              <>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="title" className="text-right">
                    Title
                  </Label>
                  <Input
                    id="title"
                    type="text"
                    placeholder="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="businessUnit" className="text-right">
                    Business Unit
                  </Label>
                  <Input
                    id="businessUnit"
                    type="text"
                    placeholder="Business Unit"
                    value={businessUnit}
                    onChange={(e) => setBusinessUnit(e.target.value)}
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="phone" className="text-right">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="image" className="text-right">
                    Profile Picture URL
                  </Label>
                  <Input
                    id="image"
                    type="text"
                    placeholder="https://example.com/profile-picture.jpg"
                    value={image}
                    onChange={(e) => setImage(e.target.value)}
                    className="col-span-3"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="submit">{inviteOnly ? 'Send invitation' : 'Add User'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
